import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { Address } from './addresses/Address.js';
import { Image } from './files/Image.js';
import { Group } from './Group.js';
import { Platform } from './Platform.js';
import { RichText } from './RichText.js';
export class EventLocation extends AutoEncoder {
    name = '';
    address = null;
}
__decorate([
    field({ decoder: StringDecoder })
], EventLocation.prototype, "name", void 0);
__decorate([
    field({ decoder: Address, nullable: true })
], EventLocation.prototype, "address", void 0);
/**
 * In case we need to cache the name, to properly display information without having to fetch loads of data
 */
export class NamedObject extends AutoEncoder {
    id = '';
    name = '';
    description = '';
}
__decorate([
    field({ decoder: StringDecoder })
], NamedObject.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], NamedObject.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], NamedObject.prototype, "description", void 0);
export class EventMeta extends AutoEncoder {
    /**
     * Contains the name of the organization for displaying the event
     */
    organizationCache = null;
    description = RichText.create({});
    visible = true;
    location = null;
    /**
     * A valid membership is required for one of these default age groups
     *
     * null = no restriction
     */
    defaultAgeGroupIds = null;
    /**
     * A valid membership is required for one of these specific groups
     * (only if organizationId is set)
     *
     * null = no restriction
     */
    groups = null;
    /**
     * A valid membership is required for an organization with one of these tags
     *
     * null = no restriction (unless organizationId is set)
     */
    organizationTagIds = null;
    coverPhoto = null;
    minAge = null;
    maxAge = null;
}
__decorate([
    field({ decoder: NamedObject, nullable: true, version: 311 })
], EventMeta.prototype, "organizationCache", void 0);
__decorate([
    field({ decoder: RichText })
], EventMeta.prototype, "description", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], EventMeta.prototype, "visible", void 0);
__decorate([
    field({ decoder: EventLocation, nullable: true })
], EventMeta.prototype, "location", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), nullable: true })
], EventMeta.prototype, "defaultAgeGroupIds", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(NamedObject), nullable: true, version: 302 })
], EventMeta.prototype, "groups", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), nullable: true })
], EventMeta.prototype, "organizationTagIds", void 0);
__decorate([
    field({ decoder: Image, nullable: true })
], EventMeta.prototype, "coverPhoto", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 382 })
], EventMeta.prototype, "minAge", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 382 })
], EventMeta.prototype, "maxAge", void 0);
export class Event extends AutoEncoder {
    id;
    name = '';
    typeId = '';
    organizationId = null;
    startDate = new Date();
    endDate = new Date();
    meta = EventMeta.create({});
    group = null;
    webshopId = null;
    createdAt = new Date();
    updatedAt = new Date();
    get dateRange() {
        return Formatter.dateRange(this.startDate, this.endDate);
    }
    static group(events) {
        const queue = [];
        const currentYear = Formatter.year(new Date());
        for (const event of events) {
            const year = Formatter.year(event.startDate);
            const title = Formatter.month(event.startDate) + (year !== currentYear ? ` ${year}` : '');
            const group = queue[queue.length - 1];
            if (group && group.title === title) {
                group.events.push(event);
            }
            else {
                queue.push({ title, events: [event] });
            }
        }
        return queue;
    }
    get eventNotificationTypes() {
        return Platform.shared.config.eventNotificationTypes.filter(t => t.eventTypeIds.includes(this.typeId));
    }
    static decodeField(...args) {
        const result = super.decodeField.call(this, ...args);
        // Create circular reference for permission checking in the frontend
        if (result.group) {
            result.group.event = result;
        }
        return result;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Event.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Event.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Event.prototype, "typeId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Event.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Event.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Event.prototype, "endDate", void 0);
__decorate([
    field({ decoder: EventMeta })
], Event.prototype, "meta", void 0);
__decorate([
    field({ decoder: Group, nullable: true })
], Event.prototype, "group", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 378 })
], Event.prototype, "webshopId", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Event.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Event.prototype, "updatedAt", void 0);
//# sourceMappingURL=Event.js.map